import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import { Link } from 'react-router-dom';
import { history } from '../../../Helpers/history';

const TIMELINE_START = 6;  // 06:00
const TIMELINE_END = 20;   // 20:00
const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START; // 14 hours
const CLEANING_GAP_MINS = 15;

const STATUS_COLORS = {
  booked: '#2196F3',
  scheduled: '#2196F3',
  in_progress: '#4CAF50',
  completed: '#9E9E9E',
  cancelled: '#F44336',
  available: 'transparent',
};

const STATUS_LABELS = {
  booked: 'Scheduled',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const LEGEND_STATUSES = ['booked', 'in_progress', 'completed', 'cancelled'];

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
  },
  legend: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.75rem',
    color: '#555',
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },
  timelineWrapper: {
    overflowX: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
  },
  timelineInner: {
    minWidth: 900,
    position: 'relative',
  },
  // Header row with time labels
  headerRow: {
    display: 'flex',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  theatreLabelHeader: {
    width: 120,
    minWidth: 120,
    flexShrink: 0,
    padding: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#666',
    borderRight: '1px solid #e0e0e0',
  },
  timeLabelsRow: {
    flex: 1,
    display: 'flex',
    position: 'relative',
  },
  timeLabel: {
    flex: 1,
    textAlign: 'left',
    padding: '8px 0',
    paddingLeft: 4,
    fontSize: '0.7rem',
    fontWeight: 500,
    color: '#888',
    borderLeft: '1px solid #e8e8e8',
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  // Theatre rows
  theatreRow: {
    display: 'flex',
    borderBottom: '1px solid #f0f0f0',
    minHeight: 56,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#fcfcfc',
    },
  },
  theatreLabel: {
    width: 120,
    minWidth: 120,
    flexShrink: 0,
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  theatreName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#333',
  },
  theatreLocation: {
    fontSize: '0.65rem',
    color: '#999',
    marginTop: 2,
  },
  timelineArea: {
    flex: 1,
    position: 'relative',
    minHeight: 56,
  },
  // Hour grid lines
  hourLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: '#f0f0f0',
    zIndex: 0,
  },
  // Now line
  nowLine: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: '#F44336',
    zIndex: 3,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: -3,
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: '#F44336',
    },
  },
  // Surgery block
  surgeryBlock: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    borderRadius: 4,
    color: '#fff',
    padding: '2px 6px',
    overflow: 'hidden',
    cursor: 'pointer',
    zIndex: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    transition: 'box-shadow 0.15s, transform 0.15s',
    textDecoration: 'none',
    '&:hover': {
      boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
      transform: 'translateY(-1px)',
      zIndex: 2,
    },
  },
  blockInitials: {
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  blockProcedure: {
    fontSize: '0.6rem',
    fontWeight: 400,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  blockSurgeon: {
    fontSize: '0.55rem',
    opacity: 0.85,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Cleaning gap
  cleaningGap: {
    position: 'absolute',
    top: 6,
    bottom: 6,
    zIndex: 1,
    borderRadius: 2,
    background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 6px)',
    border: '1px dashed #ddd',
  },
  emptyState: {
    padding: 40,
    textAlign: 'center',
    color: '#999',
    fontSize: '0.9rem',
  },
}));

// Helpers

function parseTimeRange(timeStr) {
  // "08:00-09:30" -> { startHour: 8, startMin: 0, endHour: 9, endMin: 30 }
  const [start, end] = timeStr.split('-');
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return {
    startHour: sh + sm / 60,
    endHour: eh + em / 60,
  };
}

function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function truncate(str, maxLen) {
  if (!str) return '';
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
}


const TheatreDayView = ({ date, calendarData, theatres, onSlotClick }) => {
  const classes = useStyles();

  const dayData = useMemo(() => {
    if (calendarData && calendarData.slots) {
      return calendarData;
    }
    return { date: '', slots: [] };
  }, [calendarData]);

  // Group slots by theatre
  const slotsByTheatre = useMemo(() => {
    const grouped = {};
    (theatres || []).forEach((t) => {
      grouped[t.name] = [];
    });
    (dayData.slots || []).forEach((slot) => {
      if (slot.status === 'available') return;
      if (!grouped[slot.theatre]) {
        grouped[slot.theatre] = [];
      }
      grouped[slot.theatre].push(slot);
    });
    // Sort each theatre's slots by start time
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const aTime = parseTimeRange(a.time).startHour;
        const bTime = parseTimeRange(b.time).startHour;
        return aTime - bTime;
      });
    });
    return grouped;
  }, [dayData, theatres]);

  // Current time position for "now" line
  const nowPosition = useMemo(() => {
    const now = new Date();
    const hours = now.getHours() + now.getMinutes() / 60;
    if (hours < TIMELINE_START || hours > TIMELINE_END) return null;
    // Only show if viewing today
    const d = date instanceof Date ? date : new Date(date);
    const today = new Date();
    if (d.toDateString() !== today.toDateString()) return null;
    return ((hours - TIMELINE_START) / TIMELINE_HOURS) * 100;
  }, [date]);

  const hours = useMemo(() => {
    return Array.from({ length: TIMELINE_HOURS }, (_, i) => TIMELINE_START + i);
  }, []);

  if (!theatres || theatres.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography>No theatres available</Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Legend */}
      <div className={classes.legend}>
        {LEGEND_STATUSES.map((key) => (
          <div key={key} className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: STATUS_COLORS[key] }} />
            {STATUS_LABELS[key] || key}
          </div>
        ))}
        <div className={classes.legendItem}>
          <div
            className={classes.legendDot}
            style={{
              background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
              border: '1px dashed #ccc',
            }}
          />
          Cleaning Gap
        </div>
      </div>

      {/* Timeline */}
      <div className={classes.timelineWrapper}>
        <div className={classes.timelineInner}>
          {/* Header */}
          <div className={classes.headerRow}>
            <div className={classes.theatreLabelHeader}>Theatre</div>
            <div className={classes.timeLabelsRow}>
              {hours.map((h) => (
                <div key={h} className={classes.timeLabel}>
                  {`${String(h).padStart(2, '0')}:00`}
                </div>
              ))}
            </div>
          </div>

          {/* Theatre Rows */}
          {(theatres || []).map((theatre) => {
            const slots = slotsByTheatre[theatre.name] || [];
            return (
              <div key={theatre.theatre_id || theatre.name} className={classes.theatreRow}>
                <div className={classes.theatreLabel}>
                  <div className={classes.theatreName}>{theatre.name}</div>
                  <div className={classes.theatreLocation}>{theatre.location}</div>
                </div>
                <div
                  className={classes.timelineArea}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const pct = (e.clientX - rect.left) / rect.width;
                    const totalMins = Math.round((pct * TIMELINE_HOURS * 60) / 30) * 30;
                    const clickedHour = TIMELINE_START + Math.floor(totalMins / 60);
                    const clickedMin = totalMins % 60;
                    const startTime = `${String(clickedHour).padStart(2, '0')}:${String(clickedMin).padStart(2, '0')}`;
                    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
                    history.push(`/surgery/schedule?theatre_id=${theatre.theatre_id}&date=${dateStr}&start_time=${startTime}`);
                  }}
                >
                  {/* Hour grid lines */}
                  {hours.map((h) => (
                    <div
                      key={h}
                      className={classes.hourLine}
                      style={{ left: `${((h - TIMELINE_START) / TIMELINE_HOURS) * 100}%` }}
                    />
                  ))}

                  {/* Now line */}
                  {nowPosition !== null && (
                    <div className={classes.nowLine} style={{ left: `${nowPosition}%` }} />
                  )}

                  {/* Surgery blocks */}
                  {slots.map((slot, idx) => {
                    const { startHour, endHour } = parseTimeRange(slot.time);
                    const left = ((startHour - TIMELINE_START) / TIMELINE_HOURS) * 100;
                    const width = ((endHour - startHour) / TIMELINE_HOURS) * 100;
                    const bgColor = STATUS_COLORS[slot.status] || STATUS_COLORS.booked;
                    const initials = getInitials(slot.patient);
                    const tooltipText = `${slot.procedure || 'N/A'}\n${slot.patient || 'N/A'}\n${slot.surgeon || 'N/A'}\n${slot.time}`;

                    // Cleaning gap after this block (if not last and not cancelled)
                    const showCleaningGap = slot.status !== 'cancelled' && idx < slots.length - 1;
                    const cleaningLeft = ((endHour - TIMELINE_START) / TIMELINE_HOURS) * 100;
                    const cleaningWidth = ((CLEANING_GAP_MINS / 60) / TIMELINE_HOURS) * 100;

                    return (
                      <React.Fragment key={slot.surgery_id || idx}>
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>
                              <strong>{slot.procedure || 'N/A'}</strong>
                              {'\n'}Patient: {slot.patient || 'N/A'}
                              {'\n'}Surgeon: {slot.surgeon || 'N/A'}
                              {'\n'}Time: {slot.time}
                              {'\n'}Status: {STATUS_LABELS[slot.status] || slot.status}
                            </div>
                          }
                          arrow
                        >
                          <Link
                            to={slot.surgery_id ? `/surgery/detail/${slot.surgery_id}` : '#'}
                            className={classes.surgeryBlock}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              backgroundColor: bgColor,
                            }}
                          >
                            <span className={classes.blockInitials}>{initials}</span>
                            <span className={classes.blockProcedure}>{truncate(slot.procedure, 20)}</span>
                            <span className={classes.blockSurgeon}>{slot.surgeon}</span>
                          </Link>
                        </Tooltip>
                        {showCleaningGap && (
                          <Tooltip title="Cleaning / Turnaround (15 min)" arrow>
                            <div
                              className={classes.cleaningGap}
                              style={{
                                left: `${cleaningLeft}%`,
                                width: `${cleaningWidth}%`,
                              }}
                            />
                          </Tooltip>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TheatreDayView;
